/**
 * State + actions for the Experience Workbook, shared by every surface that
 * mounts it (the borrower sidebar's Experience tab today). The deal's state
 * lives in `loanFetchedData.experience` so two mounts read one copy.
 *
 * Every mutating call replaces `items` with the server's full list (the
 * contract), and — because verify / associations re-run the loan's rollups
 * (`borrowers[].verifiedExperienceTotal`, `totalVerifiedExperience`) — the
 * loan itself is re-read afterwards so the header numbers stay the loan's.
 */
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { experienceApi } from "../../../api/experienceApi";
import { experienceAppliesToProduct, itemPoints } from "./experienceConstants";
import { useIsAcquisitions } from "../../../pipelines/PipelineContext";
import { loanFetchedDataExperienceSet } from "../../../redux/reducers/loanFetchedDataReducer";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";

const detailOf = (err, fallback) =>
  err?.response?.data?.detail?.error ||
  (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : null) ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

export const useExperienceWorkbook = (dealId) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { getLoanById } = useUnderwritingHook();
  const isAcquisitions = useIsAcquisitions();
  // Only read for the acquisitions gate below — preclose takes the server's word.
  const loanProductType = useSelector((s) => s.loanDrilldown?.loanProductType);
  const state = useSelector((s) => s.loanFetchedData?.experience);
  const mine = state?.dealId === dealId ? state : null;
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // "populate" | "extract" | "create" | itemId | null

  const load = useCallback(() => {
    if (!dealId) return Promise.resolve();
    setLoading(true);
    // `/check` carries the flags + the loan-level count; `/items` is the list
    // with `profitEstimate` expanded. One state from both.
    return Promise.all([experienceApi.check(dealId), experienceApi.items(dealId)])
      .then(([check, list]) =>
        dispatch(
          loanFetchedDataExperienceSet({
            dealId,
            items: Array.isArray(list?.items) ? list.items : check?.items || [],
            experienceApplies: (list?.experienceApplies ?? check?.experienceApplies) !== false,
            experienceChatEnabled: !!check?.experienceChatEnabled,
            totalVerifiedExperience: check?.totalVerifiedExperience ?? 0,
          }),
        ),
      )
      .catch((err) => {
        dispatch(
          loanFetchedDataExperienceSet({ dealId, items: [], experienceApplies: true, error: true }),
        );
        enqueueSnackbar(detailOf(err, "Couldn't load the experience workbook"), { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [dealId, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (!dealId || mine) return;
    load();
  }, [dealId, mine, load]);

  /** Re-read the loan so the rollup fields the header shows are the loan's own. */
  const refreshLoan = useCallback(() => {
    if (!dealId) return;
    getLoanById({
      loanId: dealId,
      onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
    });
  }, [dealId, getLoanById, dispatch]);

  /** Replace the list from a response, recount, and refresh the loan's rollups. */
  const takeItems = useCallback(
    (items) => {
      if (!Array.isArray(items)) return;
      dispatch(
        loanFetchedDataExperienceSet({
          ...(mine || { dealId, experienceApplies: true }),
          dealId,
          items,
          // POINTS, not rows. Since the line-item classifier (2026-09-22) an
          // income-producing purchase and third-party GC work count 0.5, so
          // counting rows made the header read 1 where the loan said 0.5.
          // Mirror the backend's math exactly (workbook §3).
          totalVerifiedExperience: items
            .filter((it) => it?.verified && it?.countedForExperience !== false)
            .reduce((sum, it) => sum + itemPoints(it), 0),
        }),
      );
      refreshLoan();
    },
    [dispatch, mine, dealId, refreshLoan],
  );

  const run = useCallback(
    async (key, fn, success) => {
      setBusy(key);
      try {
        const out = await fn();
        if (success)
          enqueueSnackbar(typeof success === "function" ? success(out) : success, {
            variant: "success",
          });
        return out;
      } catch (err) {
        enqueueSnackbar(detailOf(err, "The request failed"), { variant: "error" });
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [enqueueSnackbar],
  );

  const createItem = useCallback(
    (body) =>
      run(
        "create",
        async () => {
          const items = await experienceApi.create(dealId, body);
          takeItems(items);
          return items;
        },
        "Experience item added",
      ),
    [dealId, run, takeItems],
  );

  const updateItem = useCallback(
    (itemId, body) =>
      run(
        itemId,
        async () => {
          const items = await experienceApi.update(dealId, itemId, body);
          takeItems(items);
          return items;
        },
        "Experience item saved",
      ),
    [dealId, run, takeItems],
  );

  /** Resolves to the `persistence` block so the caller can render queued / skipped prompts. */
  const verifyItem = useCallback(
    (itemId, verified = true) =>
      run(itemId, async () => {
        const out = await experienceApi.verify(dealId, itemId, verified);
        takeItems(out?.items);
        return out?.persistence || null;
      }),
    [dealId, run, takeItems],
  );

  const addAssociation = useCallback(
    (itemId, type, id) =>
      run(itemId, async () => {
        const out = await experienceApi.addAssociation(dealId, itemId, type, id);
        takeItems(out?.items);
        return out?.item;
      }),
    [dealId, run, takeItems],
  );

  const removeAssociation = useCallback(
    (itemId, type, id) =>
      run(itemId, async () => {
        const out = await experienceApi.removeAssociation(dealId, itemId, type, id);
        takeItems(out?.items);
        return out?.item;
      }),
    [dealId, run, takeItems],
  );

  const removeItem = useCallback(
    (itemId) =>
      run(
        itemId,
        async () => {
          const items = await experienceApi.remove(dealId, itemId);
          takeItems(items);
          return items;
        },
        "Experience item removed",
      ),
    [dealId, run, takeItems],
  );

  const populate = useCallback(
    () =>
      run(
        "populate",
        async () => {
          const out = await experienceApi.populate(dealId);
          takeItems(out?.items);
          return out;
        },
        (out) =>
          `Pulled from track record — added ${out?.added ?? 0} · skipped ${out?.skippedExisting ?? 0} already present`,
      ),
    [dealId, run, takeItems],
  );

  const extractDocument = useCallback(
    (documentId, context) =>
      run("extract", async () => {
        const out = await experienceApi.extractDocument(dealId, documentId, context);
        if (Array.isArray(out?.items)) takeItems(out.items);
        return out;
      }),
    [dealId, run, takeItems],
  );

  // The workbook's product rule is the SAME on both pipelines (RTL family +
  // GUC). Preclose reads it off `/experience/check`; an acquisition can't,
  // because that router still resolves the deal out of `underwriting_pipeline`
  // alone and answers false for "deal not found" whatever the product is. So
  // postclose derives it from the loan's own `loanProductType` with the
  // backend's own rule. null while the product type hasn't loaded, matching
  // preclose's "unknown → don't hide it yet".
  const experienceApplies = isAcquisitions
    ? loanProductType == null
      ? null
      : experienceAppliesToProduct(loanProductType)
    : mine
      ? mine.experienceApplies !== false
      : null;

  return {
    items: mine?.items || [],
    experienceApplies,
    experienceChatEnabled: !!mine?.experienceChatEnabled,
    totalVerifiedExperience: mine?.totalVerifiedExperience ?? 0,
    loaded: !!mine,
    loading,
    busy,
    reload: load,
    createItem,
    updateItem,
    verifyItem,
    addAssociation,
    removeAssociation,
    removeItem,
    populate,
    extractDocument,
  };
};

export default useExperienceWorkbook;
